/**
 * Who this browser is, for every service that scopes by user.
 *
 * One module because three of them ask: the workspace store (`api/persistence`),
 * the journal (`api/journal`) and the journal-backed screener groups
 * (`api/symbols`). Reading the answer twice would be two answers to one
 * question, and they would disagree the first time a default changed — the
 * workspace would save under one id while the Trades groups resolved under
 * another, which reads as "my plans vanished".
 *
 * ## Signed in, the key is the account's
 *
 * The id in `/set/{user_id}` has to be the account key, or `owned_user` refuses
 * it — that endpoint proves the URL names the caller rather than trusting it.
 * With Firebase the key *is* the uid. With Descope it is a ULID minted at first
 * sight, so `trackUser` asks `/auth/me` for it before reporting anyone in.
 *
 * ## Signed out, it is the env value, exactly as before
 *
 * `VITE_QV_USER_ID` is what a developer runs on with no Firebase configured,
 * and it is why the whole app still works unauthenticated. The mutable module
 * variable is the seam between the two: it starts as the env value and is
 * replaced once Firebase says who is here.
 */

// A cycle (`api/me` reads `requestHeaders` from here), safe because neither
// module calls the other while loading.
import { fetchMe } from "../api/me";
import { actAsHeaders, actingAs, reconcileActingAs } from "./act-as";
import { authConfigured, authHeaders, authProvider, onAuthChanged } from "./session";
import { clearPass } from "./pass";

/** The fallback, for a build with no sign-in provider behind it. */
export const FALLBACK_USER_ID: string =
  // `||`, not `??`. An **empty** `VITE_QV_USER_ID` is the common way to get this
  // wrong — an unset key in a `.env` file arrives as `""`, not `undefined` — and
  // `??` would let it through as the user id, producing `?userId=` with nothing
  // after it and a 422 that names the wrong problem.
  (import.meta.env.VITE_QV_USER_ID as string | undefined) || "1";

let current: string = FALLBACK_USER_ID;

/**
 * The id to scope by, right now.
 *
 * A function rather than a constant because it changes once — when Firebase
 * reports the session it restored from storage. Modules that captured a
 * constant at import time would hold the pre-sign-in value forever, and the
 * first save would go to the wrong user.
 */
export function currentUserId(): string {
  // **Acting-as wins.** qv-data resolves the header to the effective caller, so
  // `/set/{user_id}` has to name that same account or `owned_user` refuses it —
  // that endpoint proves the URL is the caller rather than trusting it. One
  // rule here keeps the path, the query parameter and the header agreeing.
  return actingAs() ?? current;
}

/**
 * Everything a request to qv-data needs to say who it is.
 *
 * Composed in one place because the two halves must not drift: the token says
 * who is asking and `X-Act-As` says who the work is for, and a call site that
 * sent one without the other would read the wrong account's data under the
 * right person's name.
 */
export async function requestHeaders(): Promise<Record<string, string>> {
  return { ...(await authHeaders()), ...actAsHeaders() };
}

/** Re-render when the acted-as account changes. */
export { onActingAsChanged, setActingAs, actingAs } from "./act-as";

/**
 * Keep `currentUserId` in step with the session. Returns an unsubscribe.
 *
 * Called once, by the sign-in gate. Signing out restores the fallback rather
 * than leaving the last uid in place: a stale id would make the next
 * unauthenticated request look like that user.
 *
 * **The acting-as choice is reconciled first**, and that ordering matters:
 * `currentUserId` prefers the acted-as target, so clearing it afterwards would
 * leave one render in which the new account reports the old one's id. An
 * inherited target is not a small bug — it makes every request 403 with
 * *"not permitted to act as another user"* and nothing on screen to say why.
 */
export function trackUser(
  onChange?: (uid: string | null) => void,
  onError?: (message: string) => void,
): () => void {
  // Each answer supersedes the one before: an `/auth/me` still in flight for
  // an account that has since signed out must not name the next one.
  let generation = 0;
  return onAuthChanged((uid) => {
    const mine = ++generation;
    // The pass is the previous account's until `/auth/me` answers for this one.
    clearPass();
    reconcileActingAs(uid);
    if (!uid || authProvider !== "descope") {
      current = uid ?? FALLBACK_USER_ID;
      onChange?.(uid);
      return;
    }
    // **Descope's uid is not the account key.** The key is a ULID qv-data
    // mints at first sight (`ensure_from_descope`), and `/set/{user_id}` must
    // name it or `owned_user` refuses — so ask. `actorKey`, not `key`: acting
    // as is layered on by `currentUserId`, never stored here.
    void fetchMe().then((me) => {
      if (mine !== generation) return;
      if (!me?.actorKey) {
        onError?.("Signed in, but the server did not answer. Try again in a moment.");
        return;
      }
      current = me.actorKey;
      onChange?.(me.actorKey);
    });
  });
}

/** Whether this build can sign anyone in at all. */
export const authAvailable = authConfigured;

/**
 * Kept for the modules that still read it directly.
 *
 * @deprecated Use {@link currentUserId}. A constant cannot follow a sign-in,
 * which is the entire problem this module now exists to solve.
 */
export const USER_ID: string = FALLBACK_USER_ID;
